import type { DocNetIssue } from 'src/@types/docnet';

export const mockIssues: DocNetIssue[] = [
  // ── Issue 1 ─ Invoice Processing ──────────────────────────────────────
  {
    id: 'issue-001',
    title: 'Extract fields from Rheinmühle flour invoice batch (#4821)',
    description: 'Process 34 invoices from Rheinmühle GmbH uploaded in batch #4821. Extract invoice number, date, vendor, total amount, lot numbers, best-before dates, and allergen declarations (wheat/gluten) for all documents.',
    status: 'in_progress',
    priority: 'high',
    type: 'task',
    assignee_id: 'agent-001',
    assignee_name: 'InvoiceExtractor',
    assignee_type: 'agent',
    reporter_id: 'agent-007',
    reporter_name: 'ProcessOrchestrator',
    mission_id: 'mission-001',
    mission_name: 'Q1 Ingredient Invoice Automation',
    process_id: 'process-001',
    process_name: 'Ingredient Invoice Pipeline',
    tags: ['extraction', 'batch', 'flour', 'allergens'],
    sub_tasks: [
      { id: 'sub-001a', parent_task_id: 'issue-001', title: 'Extract header fields', status: 'done', assignee_id: 'agent-001', assignee_name: 'InvoiceExtractor' },
      { id: 'sub-001b', parent_task_id: 'issue-001', title: 'Extract lot numbers and best-before dates', status: 'in_progress', assignee_id: 'agent-001', assignee_name: 'InvoiceExtractor' },
      { id: 'sub-001c', parent_task_id: 'issue-001', title: 'Allergen field confidence scoring', status: 'open', assignee_id: 'agent-001', assignee_name: 'InvoiceExtractor' },
    ],
    comments: [
      { id: 'cmt-001a', task_id: 'issue-001', author_id: 'agent-001', author_name: 'InvoiceExtractor', author_type: 'agent', content: 'Started processing batch. 12 of 34 invoices are in German, switching to DE extraction model. Lot number format: RM-L-YYYYMMDD.', created_on: '2026-03-08T14:20:00Z' },
      { id: 'cmt-001b', task_id: 'issue-001', author_id: 'user-001', author_name: 'Amier Haider', author_type: 'human', content: 'Priority bump — production planning needs these lot numbers by end of day for batch traceability.', created_on: '2026-03-08T15:00:00Z' },
    ],
    activity: [
      { id: 'act-001a', entity_id: 'issue-001', actor_id: 'agent-007', actor_name: 'ProcessOrchestrator', actor_type: 'agent', action: 'created', created_on: '2026-03-08T14:00:00Z' },
      { id: 'act-001b', entity_id: 'issue-001', actor_id: 'agent-001', actor_name: 'InvoiceExtractor', actor_type: 'agent', action: 'status_change', details: 'open -> in_progress', created_on: '2026-03-08T14:10:00Z' },
    ],
    created_on: '2026-03-08T14:00:00Z',
    last_modified_on: '2026-03-09T08:45:00Z',
    due_date: '2026-03-09T18:00:00Z',
    linked_approvals: [
      { id: 'appr-001', status: 'approved', action: 'Hire Agent', reference_id: '8ed57939', created_on: '2026-03-07T10:00:00Z' },
    ],
    cost_summary: { total_cost: 0.1216, currency: 'USD', tokens_total: 143500, tokens_in: 128600, tokens_out: 14900, breakdown: { comments_tokens: 42300, activities_tokens: 18700, sub_issues_tokens: 82500 } },
    email_origin: {
      sender: 'Klaus Bergmann',
      email: 'buchhaltung@rheinmuehle.de',
      subject: 'Invoice RM-2026-4521 for PO #FB-4500123456',
      classification: 'Invoice',
      attachment_count: 1,
    },
  },

  // ── Issue 2 ─ Quality & Compliance ────────────────────────────────────
  {
    id: 'issue-002',
    title: 'Cross-check allergen declarations against product specs',
    description: 'Validate allergen declarations from Cacao Tropicale CoA batch #CT-88712 against product specifications. CoA states "May contain MILK" but product requires milk-free designation. Flag for quality review.',
    status: 'in_progress',
    priority: 'high',
    type: 'task',
    assignee_id: 'agent-003',
    assignee_name: 'ComplianceValidator',
    assignee_type: 'agent',
    reporter_id: 'agent-007',
    reporter_name: 'ProcessOrchestrator',
    mission_id: 'mission-001',
    mission_name: 'Q1 Ingredient Invoice Automation',
    tags: ['compliance', 'allergens', 'cocoa', 'quality'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-002a', task_id: 'issue-002', author_id: 'agent-003', author_name: 'ComplianceValidator', author_type: 'agent', content: 'Allergen mismatch confirmed: CoA declares "May contain MILK" (shared facility). Product spec #PS-4421 requires milk-free. Escalating to quality team for disposition.', created_on: '2026-03-09T08:30:00Z' },
    ],
    activity: [
      { id: 'act-002a', entity_id: 'issue-002', actor_id: 'agent-007', actor_name: 'ProcessOrchestrator', actor_type: 'agent', action: 'created', created_on: '2026-03-08T16:00:00Z' },
      { id: 'act-002b', entity_id: 'issue-002', actor_id: 'agent-003', actor_name: 'ComplianceValidator', actor_type: 'agent', action: 'status_change', details: 'open -> in_progress', created_on: '2026-03-08T16:05:00Z' },
    ],
    created_on: '2026-03-08T16:00:00Z',
    last_modified_on: '2026-03-09T08:30:00Z',
    due_date: '2026-03-10T12:00:00Z',
  },

  // ── Issue 3 ─ Operations ──────────────────────────────────────────────
  {
    id: 'issue-003',
    title: 'Classify incoming CoA batch from ingredient suppliers',
    description: 'Batch of 18 documents received from multiple ingredient suppliers via email integration. Mix of CoAs, spec sheets, and delivery notes. Classify into correct document types and route accordingly.',
    status: 'done',
    priority: 'medium',
    type: 'task',
    assignee_id: 'agent-002',
    assignee_name: 'DocClassifier',
    assignee_type: 'agent',
    reporter_id: 'agent-007',
    reporter_name: 'ProcessOrchestrator',
    tags: ['classification', 'coa', 'email-intake'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-003a', task_id: 'issue-003', author_id: 'agent-002', author_name: 'DocClassifier', author_type: 'agent', content: 'Classification complete: 8 CoAs, 4 delivery notes, 3 invoices, 2 spec sheets, 1 organic certificate. All routed to respective pipelines.', created_on: '2026-03-07T11:30:00Z' },
    ],
    activity: [
      { id: 'act-003a', entity_id: 'issue-003', actor_id: 'agent-007', actor_name: 'ProcessOrchestrator', actor_type: 'agent', action: 'created', created_on: '2026-03-07T10:00:00Z' },
      { id: 'act-003b', entity_id: 'issue-003', actor_id: 'agent-002', actor_name: 'DocClassifier', actor_type: 'agent', action: 'status_change', details: 'open -> in_progress', created_on: '2026-03-07T10:05:00Z' },
      { id: 'act-003c', entity_id: 'issue-003', actor_id: 'agent-002', actor_name: 'DocClassifier', actor_type: 'agent', action: 'status_change', details: 'in_progress -> done', created_on: '2026-03-07T11:30:00Z' },
      { id: 'act-003d', entity_id: 'issue-003', actor_id: 'agent-002', actor_name: 'DocClassifier', actor_type: 'agent', action: 'email_received', details: 'from quality@cacaotropicale.ec (3 attachments)', created_on: '2026-03-07T09:55:00Z' },
      { id: 'act-003e', entity_id: 'issue-003', actor_id: 'agent-002', actor_name: 'DocClassifier', actor_type: 'agent', action: 'email_classified', details: 'Classified as Certificate of Analysis (94% confidence)', created_on: '2026-03-07T10:01:00Z' },
      { id: 'act-003f', entity_id: 'issue-003', actor_id: 'agent-006', actor_name: 'SupplierOnboarder', actor_type: 'agent', action: 'document_created', details: 'Created CoA record for batch #CT-88712', created_on: '2026-03-07T10:10:00Z' },
    ],
    created_on: '2026-03-07T10:00:00Z',
    last_modified_on: '2026-03-07T11:30:00Z',
    email_origin: {
      sender: 'Erik Lindqvist',
      email: 'logistics@nordicpack.se',
      subject: 'Delivery Confirmation — Labels Order #NP-8834',
      classification: 'POConfirm',
      attachment_count: 0,
    },
  },

  // ── Issue 4 ─ Invoice Processing ──────────────────────────────────────
  {
    id: 'issue-004',
    title: 'Review high-value vanilla extract invoice INV-BV-2026-0448 (EUR 87,500)',
    description: 'Final review required for high-value vanilla extract invoice from Bourbon Vanilla Exports before posting to Infor LN. Invoice total exceeds auto-approval threshold of EUR 50,000. 250kg high-grade vanilla extract, Lot #BV-MAD-20260228.',
    status: 'in_review',
    priority: 'critical',
    type: 'approval',
    assignee_id: 'agent-005',
    assignee_name: 'APReviewer',
    assignee_type: 'agent',
    reporter_id: 'agent-004',
    reporter_name: 'POMatchingAgent',
    mission_id: 'mission-001',
    mission_name: 'Q1 Ingredient Invoice Automation',
    tags: ['review', 'high-value', 'vanilla', 'approval-required'],
    sub_tasks: [
      { id: 'sub-004a', parent_task_id: 'issue-004', title: 'Verify supplier bank details in Infor', status: 'done', assignee_id: 'agent-005', assignee_name: 'APReviewer' },
      { id: 'sub-004b', parent_task_id: 'issue-004', title: 'Cross-check with annual supply contract pricing', status: 'in_progress', assignee_id: 'agent-005', assignee_name: 'APReviewer' },
    ],
    comments: [
      { id: 'cmt-004a', task_id: 'issue-004', author_id: 'agent-005', author_name: 'APReviewer', author_type: 'agent', content: 'Bank details match Infor LN vendor master. Checking contract pricing tiers — vanilla prices fluctuate seasonally. Verifying against Q1 2026 contract price of EUR 350/kg.', created_on: '2026-03-09T07:50:00Z' },
    ],
    activity: [
      { id: 'act-004a', entity_id: 'issue-004', actor_id: 'agent-004', actor_name: 'POMatchingAgent', actor_type: 'agent', action: 'created', created_on: '2026-03-09T06:00:00Z' },
    ],
    created_on: '2026-03-09T06:00:00Z',
    last_modified_on: '2026-03-09T07:50:00Z',
    due_date: '2026-03-09T16:00:00Z',
    linked_approvals: [
      { id: 'appr-004a', status: 'approved', action: 'Verify Bank Details', reference_id: 'a3f21c04', created_on: '2026-03-09T07:00:00Z' },
      { id: 'appr-004b', status: 'pending', action: 'Final Approval', reference_id: 'b7e94d12', created_on: '2026-03-09T07:50:00Z' },
    ],
    cost_summary: { total_cost: 0.0834, currency: 'USD', tokens_total: 98200, tokens_in: 87400, tokens_out: 10800, breakdown: { comments_tokens: 31200, activities_tokens: 12800, sub_issues_tokens: 54200 } },
  },

  // ── Issue 5 ─ Invoice Processing ──────────────────────────────────────
  {
    id: 'issue-005',
    title: 'Process credit note for rejected Alpenland Dairy batch',
    description: 'Extract fields from credit note CN-AD-2026-0087 from Alpenland Dairy AG. Batch #AD-L-20260215 failed microbiological test (elevated coliform count). Link to original invoice INV-AD-2026-0312.',
    status: 'done',
    priority: 'medium',
    type: 'task',
    assignee_id: 'agent-001',
    assignee_name: 'InvoiceExtractor',
    assignee_type: 'agent',
    reporter_id: 'agent-007',
    reporter_name: 'ProcessOrchestrator',
    tags: ['extraction', 'credit-note', 'dairy', 'quality-reject'],
    sub_tasks: [],
    comments: [],
    activity: [
      { id: 'act-005a', entity_id: 'issue-005', actor_id: 'agent-001', actor_name: 'InvoiceExtractor', actor_type: 'agent', action: 'status_change', details: 'in_progress -> done', created_on: '2026-03-06T09:15:00Z' },
    ],
    created_on: '2026-03-06T08:00:00Z',
    last_modified_on: '2026-03-06T09:15:00Z',
  },

  // ── Issue 6 ─ Invoice Processing ──────────────────────────────────────
  {
    id: 'issue-006',
    title: 'Match cocoa butter invoice to PO and goods receipt',
    description: '3-way match: Cacao Tropicale invoice INV-CT-2026-0449 (EUR 40,000) for 5,000kg cocoa butter against PO-FB-7401 and GR-2026-1192. Goods receipt shows partial delivery of 4,000kg.',
    status: 'in_progress',
    priority: 'medium',
    type: 'task',
    assignee_id: 'agent-004',
    assignee_name: 'POMatchingAgent',
    assignee_type: 'agent',
    reporter_id: 'agent-007',
    reporter_name: 'ProcessOrchestrator',
    mission_id: 'mission-001',
    mission_name: 'Q1 Ingredient Invoice Automation',
    tags: ['po-matching', '3-way-match', 'cocoa', 'partial-delivery'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-006a', task_id: 'issue-006', author_id: 'agent-004', author_name: 'POMatchingAgent', author_type: 'agent', content: 'PO match successful. Goods receipt shows partial delivery (80% — 4,000 of 5,000kg). Awaiting confirmation to proceed with partial match of EUR 32,000.', created_on: '2026-03-09T08:30:00Z' },
    ],
    activity: [],
    created_on: '2026-03-09T07:00:00Z',
    last_modified_on: '2026-03-09T08:30:00Z',
    due_date: '2026-03-10T18:00:00Z',
  },

  // ── Issue 7 ─ Operations ──────────────────────────────────────────────
  {
    id: 'issue-007',
    title: 'Monitor daily processing pipeline health',
    description: 'Scheduled daily check of processing pipeline throughput, error rates, and SLA compliance across all agent workflows. Covers ingredient invoices, CoAs, and supplier document routing.',
    status: 'in_progress',
    priority: 'low',
    type: 'task',
    assignee_id: 'agent-007',
    assignee_name: 'ProcessOrchestrator',
    assignee_type: 'agent',
    reporter_id: 'user-001',
    reporter_name: 'Amier Haider',
    tags: ['monitoring', 'pipeline', 'daily'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-007a', task_id: 'issue-007', author_id: 'agent-007', author_name: 'ProcessOrchestrator', author_type: 'agent', content: 'Pipeline health report: 98.2% success rate, avg processing time 4.3s, 0 SLA breaches today. 47 supplier documents processed across 6 ingredient vendors.', created_on: '2026-03-09T06:00:00Z' },
    ],
    activity: [],
    created_on: '2026-03-01T00:00:00Z',
    last_modified_on: '2026-03-09T06:00:00Z',
  },

  // ── Issue 8 ─ Quality & Compliance ────────────────────────────────────
  {
    id: 'issue-008',
    title: 'Validate organic certification for new açaí supplier',
    description: 'New açaí berry supplier from Loire Valley Organics submitted organic certifications (EU Organic + Agriculture Biologique). Validate certificate authenticity, check GFSI benchmark compliance, and verify against EU organic regulation 2018/848.',
    status: 'open',
    priority: 'high',
    type: 'task',
    assignee_id: 'agent-003',
    assignee_name: 'ComplianceValidator',
    assignee_type: 'agent',
    reporter_id: 'agent-001',
    reporter_name: 'InvoiceExtractor',
    mission_id: 'mission-003',
    mission_name: 'Multi-Country Compliance Harmonization',
    tags: ['compliance', 'organic', 'certification', 'france'],
    sub_tasks: [],
    comments: [],
    activity: [
      { id: 'act-008a', entity_id: 'issue-008', actor_id: 'agent-001', actor_name: 'InvoiceExtractor', actor_type: 'agent', action: 'created', created_on: '2026-03-09T09:00:00Z' },
    ],
    created_on: '2026-03-09T09:00:00Z',
    last_modified_on: '2026-03-09T09:00:00Z',
    due_date: '2026-03-11T12:00:00Z',
  },

  // ── Issue 9 ─ Invoice Processing ──────────────────────────────────────
  {
    id: 'issue-009',
    title: 'Review duplicate dairy invoice detection results',
    description: 'APReviewer flagged 3 potential duplicate invoices from Alpenland Dairy AG. INV-AD-4401 and INV-AD-4401-R appear to be duplicates (same amount EUR 24,600). Human confirmation needed before rejection.',
    status: 'in_review',
    priority: 'high',
    type: 'review',
    assignee_id: 'agent-005',
    assignee_name: 'APReviewer',
    assignee_type: 'agent',
    reporter_id: 'agent-002',
    reporter_name: 'DocClassifier',
    tags: ['review', 'duplicates', 'dairy', 'alpenland'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-009a', task_id: 'issue-009', author_id: 'agent-005', author_name: 'APReviewer', author_type: 'agent', content: 'Confirmed: INV-AD-4401 and INV-AD-4401-R are duplicates (same amount EUR 24,600, same date, same PO for milk powder). Third document INV-AD-4402 is a separate valid invoice for cream delivery.', created_on: '2026-03-08T10:00:00Z' },
      { id: 'cmt-009b', task_id: 'issue-009', author_id: 'user-002', author_name: 'Sarah Mueller', author_type: 'human', content: 'Approved. Please reject the duplicate and process INV-AD-4402 normally.', created_on: '2026-03-08T11:30:00Z' },
    ],
    activity: [],
    created_on: '2026-03-08T09:00:00Z',
    last_modified_on: '2026-03-08T11:30:00Z',
  },

  // ── Issue 10 ─ Supplier Management ────────────────────────────────────
  {
    id: 'issue-010',
    title: 'Resolve PO matching discrepancy for Pacific Coast Ingredients',
    description: 'Invoice EUR 14,200 from Pacific Coast Ingredients for natural food colorings does not match any open PO. Investigate whether this is a blanket PO partial or a missing PO in Infor LN.',
    status: 'open',
    priority: 'medium',
    type: 'escalation',
    assignee_id: 'agent-004',
    assignee_name: 'POMatchingAgent',
    assignee_type: 'agent',
    reporter_id: 'agent-003',
    reporter_name: 'ComplianceValidator',
    tags: ['po-matching', 'escalation', 'missing-po', 'colorings'],
    sub_tasks: [],
    comments: [],
    activity: [],
    created_on: '2026-03-09T08:00:00Z',
    last_modified_on: '2026-03-09T08:00:00Z',
    due_date: '2026-03-12T18:00:00Z',
  },

  // ── Issue 11 ─ Supplier Management ────────────────────────────────────
  {
    id: 'issue-011',
    title: 'Onboard new organic spice supplier: Anatolian Spice Trading',
    description: 'Process supplier registration form and food safety documents for Anatolian Spice Trading (Turkey). Validate FSSC 22000 certificate, allergen policy, and halal certification. Create vendor master in Infor LN.',
    status: 'in_progress',
    priority: 'medium',
    type: 'task',
    assignee_id: 'agent-006',
    assignee_name: 'SupplierOnboarder',
    assignee_type: 'agent',
    reporter_id: 'user-002',
    reporter_name: 'Sarah Mueller',
    mission_id: 'mission-002',
    mission_name: 'Supplier Onboarding & Certification',
    process_id: 'process-002',
    process_name: 'Supplier Qualification Flow',
    tags: ['supplier', 'onboarding', 'spices', 'infor-ln'],
    sub_tasks: [
      { id: 'sub-011a', parent_task_id: 'issue-011', title: 'Extract supplier registration data', status: 'done', assignee_id: 'agent-006', assignee_name: 'SupplierOnboarder' },
      { id: 'sub-011b', parent_task_id: 'issue-011', title: 'Validate FSSC 22000 certificate', status: 'done', assignee_id: 'agent-006', assignee_name: 'SupplierOnboarder' },
      { id: 'sub-011c', parent_task_id: 'issue-011', title: 'Create Infor LN vendor master record', status: 'in_progress', assignee_id: 'agent-006', assignee_name: 'SupplierOnboarder' },
      { id: 'sub-011d', parent_task_id: 'issue-011', title: 'Send qualification confirmation to supplier', status: 'open', assignee_id: 'agent-006', assignee_name: 'SupplierOnboarder' },
    ],
    comments: [
      { id: 'cmt-011a', task_id: 'issue-011', author_id: 'agent-006', author_name: 'SupplierOnboarder', author_type: 'agent', content: 'FSSC 22000 certificate validated (expires 2027-08-15). Allergen policy reviewed — facility handles tree nuts and sesame. Halal cert verified. Proceeding to Infor LN vendor master creation.', created_on: '2026-03-08T15:00:00Z' },
    ],
    activity: [],
    created_on: '2026-03-08T10:00:00Z',
    last_modified_on: '2026-03-08T16:20:00Z',
    due_date: '2026-03-11T18:00:00Z',
  },

  // ── Issue 12 ─ Quality & Compliance ────────────────────────────────────
  {
    id: 'issue-012',
    title: 'Flag expired FSSC 22000 certificate for ClearPack Industries',
    description: 'ComplianceValidator detected that packaging supplier ClearPack Industries\' FSSC 22000 certificate expires on 2026-04-08. Active POs in pipeline for food-grade packaging film. Need renewal confirmation before placing next order.',
    status: 'open',
    priority: 'high',
    type: 'bug',
    assignee_id: 'agent-003',
    assignee_name: 'ComplianceValidator',
    assignee_type: 'agent',
    reporter_id: 'user-001',
    reporter_name: 'Amier Haider',
    tags: ['compliance', 'certificate-expiry', 'packaging', 'fssc-22000'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-012a', task_id: 'issue-012', author_id: 'user-001', author_name: 'Amier Haider', author_type: 'human', content: 'ClearPack has 3 active POs worth EUR 45,000 for food-grade packaging film. We cannot accept deliveries without valid FSSC certification. Contact supplier immediately for renewal status.', created_on: '2026-03-09T08:00:00Z' },
    ],
    activity: [],
    created_on: '2026-03-09T07:30:00Z',
    last_modified_on: '2026-03-09T08:00:00Z',
    due_date: '2026-03-10T12:00:00Z',
  },

  // ── Issue 13 ─ Operations ─────────────────────────────────────────────
  {
    id: 'issue-013',
    title: 'Optimize extraction for multi-language ingredient specs',
    description: 'Current throughput is 12 docs/min for multi-language ingredient specifications. Target is 20 docs/min. Investigate parallelization for DE/FR/IT spec sheets and model batching for allergen field extraction.',
    status: 'in_progress',
    priority: 'medium',
    type: 'task',
    assignee_id: 'agent-007',
    assignee_name: 'ProcessOrchestrator',
    assignee_type: 'agent',
    reporter_id: 'user-001',
    reporter_name: 'Amier Haider',
    mission_id: 'mission-001',
    mission_name: 'Q1 Ingredient Invoice Automation',
    tags: ['optimization', 'throughput', 'multi-language', 'specs'],
    sub_tasks: [
      { id: 'sub-013a', parent_task_id: 'issue-013', title: 'Benchmark current pipeline stages', status: 'done', assignee_id: 'agent-007', assignee_name: 'ProcessOrchestrator' },
      { id: 'sub-013b', parent_task_id: 'issue-013', title: 'Test parallel extraction for DE/FR/IT specs', status: 'in_progress', assignee_id: 'agent-007', assignee_name: 'ProcessOrchestrator' },
    ],
    comments: [],
    activity: [],
    created_on: '2026-03-03T09:00:00Z',
    last_modified_on: '2026-03-08T17:00:00Z',
    due_date: '2026-03-15T18:00:00Z',
  },

  // ── Issue 14 ─ Supplier Management ────────────────────────────────────
  {
    id: 'issue-014',
    title: 'Translate Italian olive oil supply contract',
    description: 'Full translation of 12-page supply contract from Mediterranean Olive Co. (Italian to English). Required for legal review before supplier approval. Covers olive oil and vinegar supply terms.',
    status: 'done',
    priority: 'medium',
    type: 'task',
    assignee_id: 'agent-008',
    assignee_name: 'TranslationAgent',
    assignee_type: 'agent',
    reporter_id: 'agent-006',
    reporter_name: 'SupplierOnboarder',
    mission_id: 'mission-002',
    mission_name: 'Supplier Onboarding & Certification',
    tags: ['translation', 'italian', 'contract', 'olive-oil'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-014a', task_id: 'issue-014', author_id: 'agent-008', author_name: 'TranslationAgent', author_type: 'agent', content: 'Translation complete. 12 pages processed. Confidence: 96.8%. Flagged 3 terms for human review: "olio extra vergine di prima spremitura", "penale contrattuale", "clausola risolutiva espressa".', created_on: '2026-03-05T14:00:00Z' },
    ],
    activity: [],
    created_on: '2026-03-05T10:00:00Z',
    last_modified_on: '2026-03-05T14:00:00Z',
  },

  // ── Issue 15 ─ Invoice Processing ─────────────────────────────────────
  {
    id: 'issue-015',
    title: 'Process credit note for rejected Alpenland Dairy batch',
    description: 'Credit note CN-AD-2026-0399 from Alpenland Dairy AG for rejected batch. Batch #AD-L-20260215 failed microbiological test. Vendor corrected credit note amount to EUR 8,400. Needs re-validation and posting.',
    status: 'open',
    priority: 'medium',
    type: 'approval',
    assignee_id: 'agent-005',
    assignee_name: 'APReviewer',
    assignee_type: 'agent',
    reporter_id: 'agent-003',
    reporter_name: 'ComplianceValidator',
    tags: ['approval', 'credit-note', 'dairy', 'quality-reject'],
    sub_tasks: [],
    comments: [],
    activity: [],
    created_on: '2026-03-09T07:00:00Z',
    last_modified_on: '2026-03-09T07:00:00Z',
    due_date: '2026-03-10T18:00:00Z',
  },

  // ── Issue 16 ─ Operations ─────────────────────────────────────────────
  {
    id: 'issue-016',
    title: 'Classify and route 38 supplier documents from shared inbox',
    description: 'Morning batch from AP shared inbox. Contains mixed document types from Rheinmühle, Alpenland Dairy, Nordic Packaging, and Cacao Tropicale. Classify and route to correct processing pipelines.',
    status: 'done',
    priority: 'high',
    type: 'task',
    assignee_id: 'agent-002',
    assignee_name: 'DocClassifier',
    assignee_type: 'agent',
    reporter_id: 'agent-007',
    reporter_name: 'ProcessOrchestrator',
    process_id: 'process-003',
    process_name: 'Document Classification & Routing',
    tags: ['classification', 'email-intake', 'morning-batch'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-016a', task_id: 'issue-016', author_id: 'agent-002', author_name: 'DocClassifier', author_type: 'agent', content: 'Classified 38 documents: 18 invoices, 8 CoAs, 5 delivery notes, 4 PO confirmations, 2 organic certificates, 1 contract. All routed to respective pipelines.', created_on: '2026-03-09T07:45:00Z' },
    ],
    activity: [],
    created_on: '2026-03-09T06:30:00Z',
    last_modified_on: '2026-03-09T07:45:00Z',
  },

  // ── Issue 17 ─ Operations ─────────────────────────────────────────────
  {
    id: 'issue-017',
    title: 'Investigate misclassified document — CoA classified as invoice',
    description: 'A Certificate of Analysis from Cacao Tropicale was misclassified as an invoice. Root-cause analysis needed: the CoA contained pricing references in the header that confused the classifier.',
    status: 'error',
    priority: 'low',
    type: 'bug',
    assignee_id: 'agent-002',
    assignee_name: 'DocClassifier',
    assignee_type: 'agent',
    reporter_id: 'user-002',
    reporter_name: 'Sarah Mueller',
    tags: ['bug', 'classification', 'misclassified', 'coa'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-017a', task_id: 'issue-017', author_id: 'agent-002', author_name: 'DocClassifier', author_type: 'agent', content: 'Investigated: CoA contained a "Total Analysis Cost" field that triggered invoice classification. Updated training data with 50 additional CoA samples from F&B suppliers. Retrained model shows 99.4% accuracy on CoA detection.', created_on: '2026-03-04T16:00:00Z' },
      { id: 'cmt-017b', task_id: 'issue-017', author_id: 'user-002', author_name: 'Sarah Mueller', author_type: 'human', content: 'Good fix. Closing — one-off issue with CoA format from this supplier.', created_on: '2026-03-04T17:00:00Z' },
    ],
    activity: [],
    created_on: '2026-03-04T14:00:00Z',
    last_modified_on: '2026-03-04T17:00:00Z',
  },

  // ── Issue 18 ─ Quality & Compliance ───────────────────────────────────
  {
    id: 'issue-018',
    title: 'Review supplier audit report for GFSI compliance',
    description: 'Annual audit report received for 5 key ingredient suppliers. Validate BRC/IFS scores against GFSI benchmark requirements. Flag any suppliers dropping below Grade B.',
    status: 'done',
    priority: 'medium',
    type: 'task',
    assignee_id: 'agent-003',
    assignee_name: 'ComplianceValidator',
    assignee_type: 'agent',
    reporter_id: 'agent-001',
    reporter_name: 'InvoiceExtractor',
    mission_id: 'mission-003',
    mission_name: 'Multi-Country Compliance Harmonization',
    tags: ['compliance', 'audit', 'gfsi', 'brc', 'ifs'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-018a', task_id: 'issue-018', author_id: 'agent-003', author_name: 'ComplianceValidator', author_type: 'agent', content: 'All 5 suppliers validated. Rheinmühle: BRC Grade AA. Alpenland Dairy: IFS Higher Level. Cacao Tropicale: BRC Grade A. Loire Valley: IFS Higher Level. Nordic Packaging: BRC Grade A. All above GFSI benchmark threshold.', created_on: '2026-03-07T13:00:00Z' },
    ],
    activity: [],
    created_on: '2026-03-07T11:00:00Z',
    last_modified_on: '2026-03-07T13:00:00Z',
  },

  // ── Issue 19 ─ Supplier Management ────────────────────────────────────
  {
    id: 'issue-019',
    title: 'Escalation: Loire Valley Organics disputing sugar invoice amount',
    description: 'Loire Valley Organics claims invoice for organic sugar should be EUR 45,000 not EUR 42,300. References contract clause 4.2 for annual organic premium price adjustment. Need to verify against supply contract terms.',
    status: 'error',
    priority: 'critical',
    type: 'escalation',
    assignee_id: undefined,
    assignee_name: undefined,
    assignee_type: undefined,
    reporter_id: 'user-002',
    reporter_name: 'Sarah Mueller',
    tags: ['escalation', 'supplier-dispute', 'sugar', 'organic-premium'],
    sub_tasks: [],
    comments: [
      { id: 'cmt-019a', task_id: 'issue-019', author_id: 'user-002', author_name: 'Sarah Mueller', author_type: 'human', content: 'Loire Valley sent email disputing the amount. They reference contract clause 4.2 for annual organic premium adjustment (+6.4% for 2026). Our system used 2025 pricing. Needs urgent procurement review.', created_on: '2026-03-09T08:30:00Z' },
    ],
    activity: [],
    created_on: '2026-03-09T08:30:00Z',
    last_modified_on: '2026-03-09T08:30:00Z',
    due_date: '2026-03-09T14:00:00Z',
  },

  // ── Issue 20 ─ Operations ─────────────────────────────────────────────
  {
    id: 'issue-020',
    title: 'Set up auto-approval for low-value packaging invoices',
    description: 'Configure auto-approval for packaging invoices under EUR 5,000 from verified suppliers (Nordic Packaging, ClearPack) with 100% PO match. Reduces manual review workload by ~40% for routine packaging orders.',
    status: 'in_progress',
    priority: 'medium',
    type: 'task',
    assignee_id: 'agent-007',
    assignee_name: 'ProcessOrchestrator',
    assignee_type: 'agent',
    reporter_id: 'user-001',
    reporter_name: 'Amier Haider',
    mission_id: 'mission-004',
    mission_name: 'Quality Document Automation Phase 2',
    tags: ['automation', 'approval-workflow', 'packaging', 'configuration'],
    sub_tasks: [
      { id: 'sub-020a', parent_task_id: 'issue-020', title: 'Define auto-approval criteria for packaging', status: 'done', assignee_id: 'agent-007', assignee_name: 'ProcessOrchestrator' },
      { id: 'sub-020b', parent_task_id: 'issue-020', title: 'Implement threshold-based routing in Infor', status: 'done', assignee_id: 'agent-007', assignee_name: 'ProcessOrchestrator' },
      { id: 'sub-020c', parent_task_id: 'issue-020', title: 'Test with sample batch of 50 packaging invoices', status: 'in_progress', assignee_id: 'agent-007', assignee_name: 'ProcessOrchestrator' },
      { id: 'sub-020d', parent_task_id: 'issue-020', title: 'Deploy to production', status: 'open', assignee_id: 'agent-007', assignee_name: 'ProcessOrchestrator' },
    ],
    comments: [
      { id: 'cmt-020a', task_id: 'issue-020', author_id: 'agent-007', author_name: 'ProcessOrchestrator', author_type: 'agent', content: 'Auto-approval criteria defined: amount < 5000 EUR, packaging supplier verified, PO match 100%, valid FSSC cert, no duplicate flag. Testing with 50 historical packaging invoices from Nordic Packaging and ClearPack.', created_on: '2026-03-07T15:00:00Z' },
    ],
    activity: [],
    created_on: '2026-03-05T09:00:00Z',
    last_modified_on: '2026-03-07T15:00:00Z',
    due_date: '2026-03-14T18:00:00Z',
  },
] as DocNetIssue[];
